<!--
  SupportRating.svelte — the customer's satisfaction rating, 1 to 5 stars.

  Two faces: an input when the ticket may still be rated (resolved or closed,
  requester only, once — the server decides and says so through `canRate`), and
  a read-only row of stars (`SupportStars`) once it has been. The comment is optional, since a
  star is already the answer and a required essay would cost ratings.
-->
<script lang="ts">
	import { Button, Spinner, Textarea } from 'flowbite-svelte';
	import SupportStars from './SupportStars.svelte';
	import SupportText from './SupportText.svelte';
	import { t } from '../i18n';

	interface Props {
		/** The rating already given, or `null`. */
		rating: number | null;
		/** The comment given with it. */
		comment?: string | null;
		/** Whether a rating may be given now. */
		editable?: boolean;
		/** Whether a submit is in flight. */
		submitting?: boolean;
		/** Receives the rating. */
		onrate?: (_rating: number, _comment: string) => void;
	}

	let { rating, comment = null, editable = false, submitting = false, onrate }: Props = $props();

	/** The stars picked so far. */
	let picked = $state(0);
	/** The star under the pointer, for the hover preview. */
	let hovered = $state(0);
	/** The optional comment. */
	let draft = $state('');

	/** Words for each star, read out to screen readers and shown beside the stars. */
	const LABELS = ['', 'Very dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very satisfied'];
</script>

{#if editable}
	<div class="space-y-3">
		<p class="text-sm font-medium">{$t('How did we do?')}</p>
		<div class="flex items-center gap-1" role="radiogroup" aria-label={$t('Rating')}>
			{#each [1, 2, 3, 4, 5] as star (star)}
				<button
					type="button"
					role="radio"
					aria-checked={picked === star}
					aria-label={$t(LABELS[star])}
					class="text-2xl transition {star <= (hovered || picked) ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600'}"
					onmouseenter={() => (hovered = star)}
					onmouseleave={() => (hovered = 0)}
					onclick={() => (picked = star)}
				>
					<i class="fa-solid fa-star"></i>
				</button>
			{/each}
			{#if hovered || picked}
				<span class="text-default ms-2 text-sm">{$t(LABELS[hovered || picked])}</span>
			{/if}
		</div>
		{#if picked > 0}
			<Textarea
				rows={2}
				divClass="!w-full"
				class="!w-full"
				bind:value={draft}
				placeholder={$t('Anything you would like to add? (optional)')}
				aria-label={$t('Comment')}
			/>
			<Button size="sm" disabled={submitting} onclick={() => onrate?.(picked, draft)}>
				{#if submitting}<Spinner size="4" class="me-2" />{/if}{$t('Submit rating')}
			</Button>
		{/if}
	</div>
{:else if rating !== null}
	<div class="space-y-2">
		<div class="flex items-center gap-1">
			<SupportStars {rating} size="md" />
			<span class="text-default ms-2 text-sm">{$t(LABELS[rating] ?? '')}</span>
		</div>
		{#if comment}
			<SupportText text={comment} class="text-default text-sm italic" />
		{/if}
	</div>
{/if}
